import { useState } from "react";
import { useReveal } from "@/hooks/useReveal";
import { submitInvitation } from "@/lib/api";
import { toast } from "sonner";

const INTERESTS = [
  "The Falcon Heart",
  "Private Aviation",
  "Monaco '26 Invitation",
  "Private Acquisition",
  "Diamonds",
  "Heritage Collection",
  "General Inquiry",
];

const initialState = { full_name: "", email: "", country: "", interest: "", message: "" };

export default function InvitationSection() {
  const eyebrow = useReveal();
  const title = useReveal();
  const lead = useReveal();
  const formR = useReveal();

  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.country || !form.interest) {
      toast.error("Please complete every field.");
      return;
    }
    setSubmitting(true);
    try {
      await submitInvitation(form);
      setSubmitted(true);
      setForm(initialState);
      toast.success("Thank you for your inquiry. A PiérrecDavís concierge will respond personally within 72 hours.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Unable to submit. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Unable to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="invitation"
      data-testid="invitation-section"
      className="relative py-28 md:py-40 overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse at top, rgba(212,175,140,0.08) 0%, transparent 55%), #000",
      }}
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-14">
          <p ref={eyebrow} className="pd-reveal eyebrow" data-testid="inv-eyebrow">
            Available by invitation only
          </p>
          <h2
            ref={title}
            className="pd-reveal mt-6 font-serif-display text-4xl md:text-6xl lg:text-7xl text-white tracking-[0.06em] leading-[1.05]"
            style={{ transitionDelay: "160ms" }}
            data-testid="inv-title"
          >
            Request Invitation.
          </h2>
          <div ref={lead} className="pd-reveal mt-8 max-w-2xl mx-auto" style={{ transitionDelay: "320ms" }}>
            <p className="font-serif-body italic text-lg md:text-xl text-white/80 leading-relaxed">
              Private access to the PiérrecDavís collection.
            </p>
            <p className="mt-4 text-white/55 text-sm leading-relaxed">
              A maison concierge will respond personally. All inquiries are held in confidence.
            </p>
          </div>
        </div>

        {submitted ? (
          <SubmittedConfirmation onReset={() => setSubmitted(false)} />
        ) : (
          <form
            ref={formR}
            onSubmit={handleSubmit}
            noValidate
            data-testid="invitation-form"
            className="pd-reveal grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 border border-[var(--pd-border)] bg-black/40 backdrop-blur-sm p-8 md:p-14"
          >
            <Field label="Full Name" testId="inv-field-name">
              <input
                type="text"
                value={form.full_name}
                onChange={update("full_name")}
                className="pd-input"
                placeholder="Your name"
                data-testid="inv-input-name"
                required
              />
            </Field>

            <Field label="Email Address" testId="inv-field-email">
              <input
                type="email"
                value={form.email}
                onChange={update("email")}
                className="pd-input"
                placeholder="you@example.com"
                data-testid="inv-input-email"
                required
              />
            </Field>

            <Field label="Country" testId="inv-field-country">
              <input
                type="text"
                value={form.country}
                onChange={update("country")}
                className="pd-input"
                placeholder="Country of residence"
                data-testid="inv-input-country"
                required
              />
            </Field>

            <Field label="Interest" testId="inv-field-interest">
              <select
                value={form.interest}
                onChange={update("interest")}
                className="pd-input bg-black"
                data-testid="inv-input-interest"
                required
              >
                <option value="" disabled className="bg-black">Select an area of interest</option>
                {INTERESTS.map((i) => (
                  <option key={i} value={i} className="bg-black text-white">
                    {i}
                  </option>
                ))}
              </select>
            </Field>

            <div className="md:col-span-2">
              <Field label="Message (optional)" testId="inv-field-message">
                <textarea
                  value={form.message}
                  onChange={update("message")}
                  className="pd-input resize-none"
                  rows={3}
                  placeholder="A few words for the concierge"
                  data-testid="inv-input-message"
                />
              </Field>
            </div>

            <div className="md:col-span-2 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pt-4">
              <p className="text-white/45 text-xs uppercase tracking-[0.32em]">
                Replies are sent within 72 hours
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="inv-submit-btn"
                className="btn-gold disabled:opacity-60 disabled:cursor-wait"
              >
                <span>{submitting ? "Sending…" : "Request Invitation"}</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <span className="block eyebrow mb-3">{label}</span>
      {children}
    </label>
  );
}

function SubmittedConfirmation({ onReset }) {
  return (
    <div
      data-testid="invitation-success"
      className="border border-[var(--pd-gold)] bg-black/50 backdrop-blur-sm p-10 md:p-16 text-center"
    >
      <p className="eyebrow text-[var(--pd-gold)]">Received in Confidence</p>
      <h3 className="mt-6 font-serif-display text-3xl md:text-5xl text-white tracking-[0.08em]">
        Thank you.
      </h3>
      <p className="mt-6 text-white/70 max-w-xl mx-auto leading-relaxed">
        Your request has reached the maison. A concierge will respond to you personally,
        within the privacy that defines PiérrecDavís.
      </p>
      <button onClick={onReset} className="btn-ghost mt-10" data-testid="inv-reset-btn">
        Submit Another Request
      </button>
    </div>
  );
}
